import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { Action, Mutation } from 'vuex-module-decorators';

// Placing interfaces here to remove console error
export interface ISectionTypes {
    selected:boolean;
    id: number;
    sectionTitle: string;
    options: ISectionOptionType[]
}

export interface ISectionOptionType{
    id: number;
    title: string;
    navTitle: string;
    selected : boolean;
    menuItems: ISectionMenuItem[]
}
export interface ISectionMenuItem {
    id: string;
    route: string;
    title: string;
    completed : boolean
}

@InjectModule({ stateFactory: true }, module)
export default class SectionModule extends InjectVuexModule {
    public SECTIONS: ISectionTypes[] = [
        {
            id: 111,
            sectionTitle: 'Patient',
            selected: false,
            options: [
                {
                    id: 1,
                    title: 'Patient',
                    navTitle: 'Patient',
                    selected: false,
                    menuItems: [
                        {
                            id: 'a',
                            title: 'Getting Started',
                            route: '/patient/',
                            completed: false,
                        },
                        {
                            id: 'b',
                            title: 'Patient Information',
                            route: '/patient/patient-information/',
                            completed: false,
                        },
                        {
                            id: 'c',
                            title: 'Insurance Information',
                            route: '/patient/insurance-information/',
                            completed: false,
                        },
                        {
                            id: 'l',
                            title: 'Prescriber Information',
                            route: '/patient/prescriber-information/',
                            completed: false,
                        },
                        {
                            id: 'm',
                            title: 'Patient Support Services',
                            route: '/patient/patient-support-services/',
                            completed: false,
                        },
                        {
                            id: 'q',
                            title: ' GSK Patient Assistance Program',
                            route: '/patient/assistance-program/',
                            completed: false,
                        },
                    ],
                },
            ],
        },
        {
            id: 222,
            sectionTitle: 'HCP',
            selected: false,
            options: [
                {
                    id: 2,
                    title: 'Health Care Provider',
                    navTitle: 'Health Care Provider',
                    selected: false,
                    menuItems: [
                        {
                            id: 'd',
                            title: 'Getting Started',
                            route: '/hcp/',
                            completed: false,
                        },
                        {
                            id: 'e',
                            title: 'Patient Information',
                            route: '/hcp/patient-information/',
                            completed: false,
                        },
                        {
                            id: 'f',
                            title: 'Insurance Information',
                            route: '/hcp/insurance-information/',
                            completed: false,
                        },
                        {
                            id: 'g',
                            title: 'Eye Care Support',
                            route: '/hcp/eye-care-support/',
                            completed: false,
                        },                        {
                            id: 'h',
                            title: 'GSK Patient Assistance Program',
                            route: '/hcp/assistance-program/',
                            completed: false,
                        },
                        {
                            id: 'o',
                            title: 'Prescriber/Site Information',
                            route: '/hcp/prescriber-site-information/',
                            completed: false,
                        },
                        {
                            id: 'p',
                            title: 'Clinical Information',
                            route: '/hcp/clinical-information/',
                            completed: false,
                        },

                    ],
                },

            ],
        },
        {
            id: 333,
            sectionTitle: 'Document Upload',
            selected: false,
            options: [
                {
                    id: 3,
                    title: 'Document Upload',
                    navTitle: 'Document Upload',
                    selected: false,
                    menuItems: [
                        {
                            id: 'i',
                            title: 'Getting Started',
                            route: '/upload/',
                            completed: false,
                        },
                        {
                            id: 'j',
                            title: 'Document Upload',
                            route: '/upload-document',
                            completed: false,
                        },
                    ],
                },
            ],
        },
    ];
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    public isPssFlow: boolean = false;
    public get isPatientPath() {
        const patientPathId = 111;
        return this.SECTIONS.find(path => path.id === patientPathId)?.selected;
    }

    public get isHcpPath() {
        const HcpPathId = 222;
        return this.SECTIONS.find(path => path.id === HcpPathId)?.selected;
    }

    public get isPharmacytPath() {
        const pharmacyPathId = 333;
        return this.SECTIONS.find(path => path.id === pharmacyPathId)?.selected;
    }

    public get anyPathSelected() {
        return this.SECTIONS.some(section => section.options.some(option => option.selected));
    }

    public get nothingSelected() {
        return this.SECTIONS.every(section => !section.selected);
    }

    public get activeSection() {
        return this.SECTIONS.map(section => ({
            ...section,
            options: section.options.filter(option => option.selected),
        })).filter(section => section.selected || section.options.length > 0);
    }

    get activeUniqueMenuItems(): ISectionOptionType {
        const uniqueMenuItemsSet = new Set<string>();
        const activeOptions: ISectionOptionType[] = this.SECTIONS.flatMap(section =>
            section.options
                .filter(option => option.selected)
                .map(option => ({
                    ...option,
                    menuItems: option.menuItems.filter(menuItem => {
                        const isUnique = !uniqueMenuItemsSet.has(menuItem.id);
                        if (isUnique) uniqueMenuItemsSet.add(menuItem.id);
                        return isUnique;
                    }),
                }))
                .filter(option => option.menuItems.length > 0)
                .sort((a, b) => (a.id > b.id ? 1 : a.id < b.id ? -1 : 0))
        );
        return activeOptions[0];
    }

    @Mutation
    public resetSelections() {
        this.SECTIONS.forEach(element => {
            element.selected = false;
            element.options.forEach(option => {
                option.selected = false;
                option.menuItems.forEach(menu => {
                    menu.completed = false;
                });
            });
        });
    }

    @Mutation
    public handleSelectionPath({ section, optionIndex }: { section: ISectionTypes; optionIndex: number }) {
        this.SECTIONS.forEach(element => {
            element.selected = false;
            element.options.forEach(option => {
                option.selected = false;
            });
        });
        this.isPssFlow = section.options[optionIndex].navTitle === 'Health Care Provider';
        section.options[optionIndex].selected = true;
        section.selected = true;
    }

    @Mutation
    public resetPssFlow() {
        this.isPssFlow = false;
    }

    @Mutation
    public enableSection(section: ISectionTypes) {
        this.SECTIONS.forEach(element => {
            if (element.id !== section.id) {
                element.selected = false;
                element.options.forEach(option => {
                    option.selected = false;
                });
            }
        });
        section.selected = true;
    }

    @Mutation
    public completeMenuItem(menuItem: ISectionMenuItem) {
        menuItem.completed = true;
    }

    @Action
    public nextMenuItem(route: string) {
        const allMenuItemsWithHeading: ISectionOptionType = this.activeUniqueMenuItems;
        const idx = allMenuItemsWithHeading.menuItems.findIndex(obj => obj.route === route);
        if (idx !== -1 && idx < allMenuItemsWithHeading.menuItems.length) {
            this.completeMenuItem(allMenuItemsWithHeading.menuItems[idx]);
            if (idx < allMenuItemsWithHeading.menuItems.length - 1) {
                return allMenuItemsWithHeading.menuItems[idx + 1].route;
            } else {
                return undefined;
            }
        } else {
            return undefined;
        }
    }

    @Action
    public prevoiusMenuItem(route: string) {
        const allMenuItemsWithHeading: ISectionOptionType = this.activeUniqueMenuItems;
        const idx = allMenuItemsWithHeading.menuItems.findIndex(obj => obj.route === route);
        if (idx > 0) {
            return allMenuItemsWithHeading.menuItems[idx - 1].route;
        } else {
            return '/';
        }
    }
}
